import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { ExpertAccessPayload, ExpertManufacturer } from './types';

export interface UseExpertAccessResult {
    enabled: boolean;
    standards_enabled: boolean;
    standardsEnabled: boolean;
    is_platform_admin: boolean;
    isPlatformAdmin: boolean;
    manufacturers: ExpertManufacturer[];
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<ExpertAccessPayload>;
}

const CLOSED_ACCESS: ExpertAccessPayload = {
    enabled: false,
    standards_enabled: true,
    is_platform_admin: false,
    manufacturers: [],
};

function normalizeManufacturer(raw: unknown): ExpertManufacturer | null {
    if (!raw || typeof raw !== 'object') return null;
    const obj = raw as Record<string, unknown>;
    const id =
        typeof obj.id === 'string'
            ? obj.id.trim()
            : typeof obj.manufacturer_id === 'string'
              ? obj.manufacturer_id.trim()
              : '';
    const displayName =
        typeof obj.display_name === 'string'
            ? obj.display_name.trim()
            : typeof obj.name === 'string'
              ? obj.name.trim()
              : '';
    if (!id || !displayName) return null;

    return {
        id,
        slug: typeof obj.slug === 'string' ? obj.slug.trim() : undefined,
        display_name: displayName,
        logo_url: typeof obj.logo_url === 'string' && obj.logo_url.trim() ? obj.logo_url.trim() : null,
        aliases: Array.isArray(obj.aliases)
            ? obj.aliases.filter((a): a is string => typeof a === 'string')
            : undefined,
        tech_support_phone:
            typeof obj.tech_support_phone === 'string' && obj.tech_support_phone.trim()
                ? obj.tech_support_phone.trim()
                : null,
        tech_support_hours:
            typeof obj.tech_support_hours === 'string' && obj.tech_support_hours.trim()
                ? obj.tech_support_hours.trim()
                : null,
        tech_support_email:
            typeof obj.tech_support_email === 'string' && obj.tech_support_email.trim()
                ? obj.tech_support_email.trim()
                : null,
        tech_support_url:
            typeof obj.tech_support_url === 'string' && obj.tech_support_url.trim()
                ? obj.tech_support_url.trim()
                : null,
        trade_expert_enabled:
            typeof obj.trade_expert_enabled === 'boolean' ? obj.trade_expert_enabled : true,
    };
}

export function parseExpertAccessPayload(data: unknown): ExpertAccessPayload {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return { ...CLOSED_ACCESS, manufacturers: [] };
    }
    const obj = data as Record<string, unknown>;
    const enabled = obj.enabled === true;
    const standardsEnabled =
        typeof obj.standards_enabled === 'boolean' ? obj.standards_enabled : true;
    const isPlatformAdmin = obj.is_platform_admin === true;
    const rawList = Array.isArray(obj.manufacturers) ? obj.manufacturers : [];
    const manufacturers = enabled
        ? rawList
              .map(normalizeManufacturer)
              .filter((m): m is ExpertManufacturer => m !== null)
        : [];

    return {
        enabled,
        standards_enabled: standardsEnabled,
        is_platform_admin: isPlatformAdmin,
        manufacturers,
    };
}

export function useExpertAccess(): UseExpertAccessResult {
    const [access, setAccess] = useState<ExpertAccessPayload>(CLOSED_ACCESS);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchAccess = useCallback(async (): Promise<ExpertAccessPayload> => {
        setIsLoading(true);
        try {
            if (typeof supabase?.rpc !== 'function') {
                setAccess(CLOSED_ACCESS);
                setError(null);
                return CLOSED_ACCESS;
            }
            const res = await supabase.rpc('get_expert_access');
            if (res?.error) {
                throw new Error(res.error.message || 'Could not verify Technical Expert access.');
            }
            const parsed = parseExpertAccessPayload(res?.data);
            setAccess(parsed);
            setError(null);
            return parsed;
        } catch (err: unknown) {
            const msg =
                err instanceof Error
                    ? err.message
                    : 'Could not verify Technical Expert access.';
            setAccess(CLOSED_ACCESS);
            setError(msg);
            return CLOSED_ACCESS;
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchAccess();
    }, [fetchAccess]);

    return {
        enabled: access.enabled,
        standards_enabled: access.standards_enabled,
        standardsEnabled: access.standards_enabled,
        is_platform_admin: access.is_platform_admin,
        isPlatformAdmin: access.is_platform_admin,
        manufacturers: access.manufacturers,
        isLoading,
        error,
        refresh: fetchAccess,
    };
}

export default useExpertAccess;
